import { Link } from "react-router-dom";
import { Eye, MapPin, BarChart3, Search, Lock, ShieldCheck, ArrowRight } from "lucide-react";
import PageHeader from "../components/PageHeader";
import Seo from "../components/Seo";

const PILLARS = [
  { icon: Eye, t: "Witnessed certification", d: "MAS built its name on certification you can witness — no black box. It's the same philosophy behind every MAS grade.", link: ["What is witnessed certification?", "/witnessed-certification"] },
  { icon: MapPin, t: "On-site convention grading", d: "MAS is bringing professional grading directly to card shows nationwide — hand over your cards, watch the process, and pick them up the same day.", link: ["Read the announcement", "/press"] },
  { icon: BarChart3, t: "Transparent live population", d: "Every MAS card is tracked in a live, public population registry, so you can see exactly how rare your card's condition truly is.", link: ["How population works", "/card-grades"] },
  { icon: Search, t: "Instant online verification", d: "Anyone, anywhere, can confirm a MAS grade in seconds by entering the cert number — no account, no friction.", link: ["Verify a cert", "/lookup"] },
  { icon: Lock, t: "Tamper-evident slabs", d: "Each card is sealed in an ultrasonically welded, tamper-evident archival holder with a unique, verifiable cert number.", link: ["The MAS standard", "/standard"] },
  { icon: ShieldCheck, t: "Backed by MAS Certified, Inc.", d: "MAS Grading is operated by MAS Certified, Inc. — a trusted name collectors, dealers, and auction houses already rely on.", link: ["About MAS Grading", "/about"] },
];

const LD = {
  "@context": "https://schema.org",
  "@type": "AboutPage",
  name: "Why MAS Grading",
  url: "https://grading.mascertified.com/why-mas",
  description: "Why collectors choose MAS Grading: witnessed certification, on-site convention grading, a transparent live population registry, instant online verification, and tamper-evident slabs.",
  isPartOf: { "@type": "WebSite", name: "MAS Grading", url: "https://grading.mascertified.com" },
};

export default function WhyMas() {
  return (
    <div className="bg-white" data-testid="why-mas-page">
      <Seo
        title="Why MAS Grading — Trusted, Transparent Card Grading"
        description="Why collectors choose MAS Grading: witnessed certification, on-site convention grading, a transparent live population registry, instant online verification, and tamper-evident slabs that protect your card's value."
        path="/why-mas"
        jsonLd={LD}
      />
      <PageHeader
        eyebrow="Why MAS"
        title="Grading you can trust — and actually verify"
        subtitle="Collectors deserve more than a number in a slab. MAS Grading is built on transparency: witnessed certification, a live public population registry, instant online verification, and grading you can watch happen in person."
        word="WHY"
      />
      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-20 md:py-28">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {PILLARS.map((p) => (
            <div key={p.t} className="rounded-3xl border border-neutral-200 bg-white p-8 flex flex-col" data-testid={`why-pillar-${p.t.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              <p.icon className="text-[#0A0A0A] mb-5" size={28} />
              <h3 className="font-display font-extrabold text-xl tracking-tight text-[#0A0A0A]">{p.t}</h3>
              <p className="text-neutral-500 mt-3 leading-relaxed text-sm flex-1">{p.d}</p>
              <Link to={p.link[1]} className="inline-flex items-center gap-1.5 mt-5 font-bold text-sm text-[#0A0A0A] underline underline-offset-4 decoration-neutral-300 hover:decoration-[#0A0A0A]">
                {p.link[0]} <ArrowRight size={14} />
              </Link>
            </div>
          ))}
        </div>

        <div className="rounded-3xl bg-[#0A0A0A] text-white p-8 md:p-14 mt-6">
          <h2 className="font-display font-extrabold text-3xl md:text-4xl tracking-tight">Protect what your collection is worth</h2>
          <p className="text-white/60 mt-4 max-w-2xl leading-relaxed">A MAS grade does more than assign a number — it makes your card's condition provable, portable, and trusted the moment you want to sell, trade, or insure it. Submit online today, or verify any existing MAS cert in seconds.</p>
          <div className="mt-8 flex flex-wrap gap-4">
            <Link to="/submit" data-testid="why-cta-submit" className="inline-flex items-center gap-2 px-8 py-4 bg-white text-[#0A0A0A] font-bold rounded-full hover:bg-neutral-200 transition-colors duration-300">
              Submit an Item <ArrowRight size={16} />
            </Link>
            <Link to="/compare" data-testid="why-cta-compare" className="inline-flex items-center gap-2 px-8 py-4 border border-white/30 text-white font-bold rounded-full hover:border-white transition-colors duration-300">
              Compare MAS vs PSA, Beckett &amp; JSA
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
